export const toolTracker = {
  patientDistance: 0,
  trackIntervalDelay: 1000,
  wearableTool: null,
  mediaElements: {},
  cv: null,
  processingDimensions: {
    width: 960,
    height: 720,
  },
  thresholdDetection: {},
  thresholdDetectionHypotenuse: {},
  thresholdOptions: ['THRESH_BINARY'],
  hypotenuse: 0,
  perceivedFocalLength: 0,
  init: (cv, wearableTool, videoDOM, canvasDOM, perceivedFocalLength) => {
    toolTracker.clearAllIntervals();
    if (cv && wearableTool && videoDOM?.current && canvasDOM?.current) {
      toolTracker.wearableTool = wearableTool;
      toolTracker.cv = cv;
      toolTracker.mediaElements = {
        video: videoDOM,
        canvas: canvasDOM,
      };
      toolTracker.perceivedFocalLength = perceivedFocalLength;

      toolTracker.start();
    }
  },
  start: () => {
    toolTracker.startCamera();
    setInterval(() => {
      toolTracker.takePicture();
      for (var thresholdLevel = 5; thresholdLevel <= 255; thresholdLevel += 10) {
        toolTracker.detectDistance(thresholdLevel);
      }
    }, toolTracker.trackIntervalDelay);
  },
  startCamera: () => {
    // When the tracker is given the call's local <video> (the patient's video tile), it already
    // plays the camera track: reuse it instead of opening a second camera stream (a second
    // getUserMedia can freeze the call's own track on iOS Safari).
    const element = toolTracker.mediaElements.video?.current;
    if (element?.srcObject) {
      if (element.paused) element.play?.().catch(() => {});
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ video: toolTracker.processingDimensions, audio: false })
      .then(function (stream) {
        const isPlaying =
          toolTracker.mediaElements.video.current.currentTime > 0 &&
          !toolTracker.mediaElements.video.current.paused &&
          !toolTracker.mediaElements.video.current.ended &&
          toolTracker.mediaElements.video.current.readyState > toolTracker.mediaElements.video.current.HAVE_CURRENT_DATA;

        if (!isPlaying) {
          toolTracker.mediaElements.video.current.srcObject = stream;
          toolTracker.mediaElements.video.current.play();
        }
      })
      .catch(function (err) {
        console.error('Error occured when streaming video: ' + err);
      });
  },
  startDetection: () => {},
  detectDistance: (thresholdLevel) => {
    if (!toolTracker.mediaElements.canvas.current) {
      toolTracker.clearAllIntervals();
    }
    toolTracker.processImage(thresholdLevel);
  },
  takePicture: () => {
    if (toolTracker.mediaElements.canvas.current && toolTracker.mediaElements.video.current) {
      toolTracker.mediaElements.canvas.current.width = toolTracker.processingDimensions.width;
      toolTracker.mediaElements.canvas.current.height = toolTracker.processingDimensions.height;
      toolTracker.mediaElements.video.current.width = toolTracker.processingDimensions.width;
      toolTracker.mediaElements.video.current.height = toolTracker.processingDimensions.height;

      if (window.innerHeight > window.innerWidth) {
        toolTracker.mediaElements.canvas.current.width = toolTracker.processingDimensions.height;
        toolTracker.mediaElements.canvas.current.height = toolTracker.processingDimensions.width;
        toolTracker.mediaElements.video.current.width = toolTracker.processingDimensions.height;
        toolTracker.mediaElements.video.current.height = toolTracker.processingDimensions.width;
      }

      const canvasContext = toolTracker.mediaElements.canvas.current.getContext('2d');
      canvasContext.drawImage(
        toolTracker.mediaElements.video.current,
        0,
        0,
        toolTracker.mediaElements.video.current.width,
        toolTracker.mediaElements.video.current.height,
      );
    }
  },
  getOpenCvThresholdMethod: (thresholdOption) => {
    switch (thresholdOption) {
      case 'THRESH_BINARY':
        return toolTracker.cv.THRESH_BINARY;
      case 'THRESH_BINARY_INV':
        return toolTracker.cv.THRESH_BINARY_INV;
      case 'THRESH_TRUNC':
        return toolTracker.cv.THRESH_TRUNC;
      case 'THRESH_TOZERO':
        return toolTracker.cv.THRESH_TOZERO;
      case 'THRESH_TOZERO_INV':
        return toolTracker.cv.THRESH_TOZERO_INV;
      case 'THRESH_OTSU':
        return toolTracker.cv.THRESH_OTSU;
      case 'THRESH_TRIANGLE':
        return toolTracker.cv.THRESH_TRIANGLE;
      case 'ADAPTIVE_THRESH_GAUSSIAN_C':
        return toolTracker.cv.ADAPTIVE_THRESH_GAUSSIAN_C;
      case 'ADAPTIVE_THRESH_MEAN_C':
        return toolTracker.cv.ADAPTIVE_THRESH_MEAN_C;
      default:
        return toolTracker.cv.THRESH_BINARY;
    }
  },
  processImage: (thresholdLevel) => {
    if (toolTracker.mediaElements.canvas.current && toolTracker.mediaElements.video.current) {
      const color = new toolTracker.cv.Scalar(255, 255, 255);
      const detectedTrianglesData = [];

      if (toolTracker.thresholdDetection[thresholdLevel]) {
        delete toolTracker.thresholdDetection[thresholdLevel];
      }

      if (toolTracker.thresholdDetectionHypotenuse[thresholdLevel]) {
        delete toolTracker.thresholdDetectionHypotenuse[thresholdLevel];
      }

      for (let i = 0; i < toolTracker.thresholdOptions.length; i++) {
        let hierarchy = new toolTracker.cv.Mat();
        let contours = new toolTracker.cv.MatVector();
        let openCvImgForProcessing = toolTracker.cv.imread(toolTracker.mediaElements.canvas.current);
        let openCvBlackImage = toolTracker.cv.Mat.zeros(openCvImgForProcessing.rows, openCvImgForProcessing.cols, toolTracker.cv.CV_8UC3);

        toolTracker.cv.cvtColor(openCvImgForProcessing, openCvImgForProcessing, toolTracker.cv.COLOR_RGBA2GRAY, 0);

        toolTracker.cv.threshold(
          openCvImgForProcessing,
          openCvImgForProcessing,
          thresholdLevel,
          255,
          toolTracker.getOpenCvThresholdMethod(toolTracker.thresholdOptions[i]),
        );

        toolTracker.cv.findContours(openCvImgForProcessing, contours, hierarchy, toolTracker.cv.RETR_TREE, toolTracker.cv.CHAIN_APPROX_SIMPLE);

        for (let j = 0; j < contours.size(); ++j) {
          let hier = hierarchy.intPtr(0, j);
          let cnt = contours.get(j);
          let perim = toolTracker.cv.arcLength(cnt, false);
          let rect = toolTracker.cv.boundingRect(cnt);
          let approx = new toolTracker.cv.Mat();

          toolTracker.cv.approxPolyDP(cnt, approx, 0.1 * perim, true);

          // get shape with 3 vertices (triangle), with no sibling contour, no child contour and has to have a parent contour
          if (approx.rows === 3 && hier[0] === -1 && hier[1] === -1 && hier[2] === -1 && hier[3] !== -1) {
            let parentContourApprox = new toolTracker.cv.Mat();
            let parentContour = contours.get(hier[3]);

            // get the number of vertices of the triangle's parent contours.
            toolTracker.cv.approxPolyDP(parentContour, parentContourApprox, 0.1 * perim, true);

            //is the parent contour a square/rectangle
            if (parentContourApprox.rows === 4) {
              if (detectedTrianglesData.length < 2) {
                detectedTrianglesData.push({
                  contour: cnt,
                  hier: hier,
                  rect: rect,
                  id: j,
                  parentContour: parentContour,
                });

                toolTracker.cv.drawContours(openCvBlackImage, contours, j, color, 2, toolTracker.cv.LINE_8, hierarchy, 100);
              }
              toolTracker.cv.drawContours(openCvImgForProcessing, contours, j, color, 2, toolTracker.cv.LINE_8, hierarchy, 100);
            }

            parentContourApprox.delete();
            parentContour.delete();
          }

          // REALLY IMPORTANT TO DELETE OPENCV OBJECTS TO AVOID MEMORY OVERLOAD
          cnt.delete();
          approx.delete();
        }

        // REALLY IMPORTANT TO DELETE OPENCV OBJECTS TO AVOID MEMORY OVERLOAD
        openCvImgForProcessing.delete();
        openCvBlackImage.delete();
        contours.delete();
        hierarchy.delete();
      }

      if (detectedTrianglesData.length === 2) {
        toolTracker.thresholdDetection[thresholdLevel] = toolTracker.getWearableToolDistance(detectedTrianglesData);
        toolTracker.patientDistance = toolTracker.thresholdDetection[thresholdLevel];
        toolTracker.thresholdDetectionHypotenuse[thresholdLevel] = toolTracker.calculateHypotenuse(detectedTrianglesData);
        toolTracker.hypotenuse = toolTracker.thresholdDetectionHypotenuse[thresholdLevel];
      }
    }
  },
  clearAllIntervals: () => {
    // Get a reference to the last interval + 1
    const interval_id = window.setInterval(function () {}, Number.MAX_SAFE_INTEGER);

    // Clear any timeout/interval up to that id
    for (let i = 1; i < interval_id; i++) {
      window.clearInterval(i);
    }
  },
  getWearableToolDistance: (detectedTrianglesData) => {
    return Math.round(
      (toolTracker.wearableTool.distanceFrontTrianglesInMm * toolTracker.perceivedFocalLength) /
        toolTracker.calculateHypotenuse(detectedTrianglesData),
    );
  },
  calculateHypotenuse: (detectedTrianglesData) => {
    let x1;
    let x2;
    let y1;
    let y2;
    if (detectedTrianglesData[0]['rect']['x'] < detectedTrianglesData[1]['rect']['x']) {
      x1 = detectedTrianglesData[0]['rect']['x'];
      x2 = detectedTrianglesData[1]['rect']['x'];
    } else {
      x1 = detectedTrianglesData[1]['rect']['x'];
      x2 = detectedTrianglesData[0]['rect']['x'];
    }

    if (detectedTrianglesData[0]['rect']['y'] < detectedTrianglesData[1]['rect']['y']) {
      y1 = detectedTrianglesData[0]['rect']['y'];
      y2 = detectedTrianglesData[1]['rect']['y'];
    } else {
      y1 = detectedTrianglesData[1]['rect']['y'];
      y2 = detectedTrianglesData[0]['rect']['y'];
    }

    return Math.hypot(x2 - x1, y2 - y1);
  },
  extractHypotenuse: () => {
    const hypotenuseToReturn = toolTracker.hypotenuse;
    toolTracker.hypotenuse = 0;
    return hypotenuseToReturn;
  },
  extractDistance: () => {
    const distanceToReturn = toolTracker.patientDistance;
    toolTracker.patientDistance = 0;
    return distanceToReturn;
  },
};
